/**
 * 用户信息管理 Composable
 * @description 从 userStore 抽取的用户资料更新、头像管理、身份验证逻辑
 */

import type { Ref } from 'vue'
import type {
  UserInfo,
  VerificationData,
} from '@/types/auth'

import { authApi } from '@/api/auth'

import { handleLoginError } from './authActions'

/**
 * 用户信息 Composable 的依赖接口
 */
export interface UserProfileComposableDeps {
  userInfo: Ref<UserInfo | null>
  setUserInfo: (info: UserInfo | null) => void
  setPermissionsFromUserInfo: (info: UserInfo | null, customPermissions?: string[]) => void
}

/**
 * 创建用户信息管理 actions
 */
export function useUserProfileActions(deps: UserProfileComposableDeps) {
  const { userInfo, setUserInfo, setPermissionsFromUserInfo } = deps

  // 更新用户信息
  async function updateUserInfo(updates: Partial<UserInfo>) {
    if (!userInfo.value) {
      return { success: false, error: 'User not logged in' }
    }

    try {
      const response = await authApi.updateProfile(updates)

      if (response.code === '00000' && response.data) {
        const oldRole = userInfo.value?.role
        setUserInfo(response.data)

        // 🔧 如果角色发生变化，重新设置权限
        if (response.data.role && response.data.role !== oldRole) {
          setPermissionsFromUserInfo(response.data)
        }

        return { success: true, data: response.data }
      }
      else {
        return { success: false, error: response.msg }
      }
    }
    catch (error: unknown) {
      const errorMsg = handleLoginError(error)
      return { success: false, error: errorMsg }
    }
  }

  // 更新用户头像
  async function updateUserAvatar(avatarUrl: string) {
    if (!userInfo.value) {
      return { success: false, error: 'User not logged in' }
    }

    try {
      // 验证头像URL格式
      if (!avatarUrl || !avatarUrl.trim()) {
        return { success: false, error: 'Invalid avatar URL' }
      }

      // 使用updateUserInfo方法更新头像
      const result = await updateUserInfo({ avatar: avatarUrl })
      return result
    }
    catch (error: unknown) {
      const errorMsg = handleLoginError(error)
      return { success: false, error: errorMsg }
    }
  }

  // 上传用户头像文件
  async function uploadUserAvatar(file: File) {
    if (!userInfo.value) {
      return { success: false, error: 'User not logged in' }
    }

    try {
      // 验证文件
      const isValidType = ['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)
      if (!isValidType) {
        return { success: false, error: '只支持 JPG、PNG 格式的头像' }
      }

      const maxSize = 2 * 1024 * 1024 // 2MB
      if (file.size > maxSize) {
        return { success: false, error: '头像大小不能超过 2MB' }
      }

      const formData = new FormData()
      // 注意：后端 FastApi 接收的参数名为 'file'
      formData.append('file', file)

      const response = await authApi.uploadAvatar(formData)

      if (response.code === '00000' && response.data) {
        setUserInfo(response.data)
        return { success: true, data: response.data }
      }
      else {
        return { success: false, error: response.msg }
      }
    }
    catch (error: unknown) {
      const errorMsg = handleLoginError(error)
      return { success: false, error: errorMsg }
    }
  }

  // 删除用户头像（恢复默认头像）
  async function removeUserAvatar() {
    if (!userInfo.value) {
      return { success: false, error: 'User not logged in' }
    }

    try {
      const result = await updateUserAvatar('')
      return result
    }
    catch (error: unknown) {
      const errorMsg = handleLoginError(error)
      return { success: false, error: errorMsg }
    }
  }

  // 验证用户身份
  // TODO: P4 阶段对接真实实名认证 API
  async function verifyUser(_verificationData: VerificationData) {
    if (!userInfo.value) {
      return { success: false, error: 'User not logged in' }
    }

    try {
      const updatedInfo = {
        ...userInfo.value,
        isVerified: true,
        updatedAt: new Date().toISOString(),
      }

      setUserInfo(updatedInfo)
      return { success: true, data: updatedInfo }
    }
    catch (error: unknown) {
      const errorMsg = handleLoginError(error)
      return { success: false, error: errorMsg }
    }
  }

  return {
    updateUserInfo,
    updateUserAvatar,
    uploadUserAvatar,
    removeUserAvatar,
    verifyUser,
  }
}
